import React, { useState, useRef, useContext, useMemo, useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { useSwipeable } from 'react-swipeable';
import classNames from 'classnames';
import _ from 'lodash';

import { RootState, Dispatch, select } from 'src/configs/store';
import AppModeContext from 'src/containers/AppMode/AppMode.context';
import VirtualKeyboardContext from 'src/components/VirtualKeyboard/Context/VirtualKeyboard.context';
import StakeOptions from '../StakeOptions/StakeOptions';
import VirtualKeyboard from 'src/components/VirtualKeyboard/VirtualKeyboard';
import SubmitButton from './SubmitButton/SubmitButton';
import BetsList from './BetsList/BetsList';
import RegisterBetButton from './RegisterBetButton/RegisterBetButton';
import Icon from 'src/components/Icon/Icon';
import BetslipTax from './BetslipTax/BetslipTax';
import RegisterBet from './RegisterBet/RegisterBet';
import BetslipPopup from './BetslipPopup/BetslipPopup';
import BetslipOptions from '../BetslipOptions/BetslipOptions';

function Betslip() {
  // MISC
  const { t } = useTranslation('common');
  const history = useHistory();

  // REDUX
  const betsLength = useSelector((state: RootState) => state.betslip.bets.length);
  const bets = useSelector((state: RootState) => state.betslip.bets);
  const shouldChangeSlip = useSelector((state: RootState) => {
    const comboBets = _.filter(state.betslip.bets, (bet) => bet.permutationsCount > 1);
    return comboBets.length === 0;
  });
  const slip = useSelector((state: RootState) => state.betslip.slip);
  const systemSlip = useSelector((state: RootState) => state.betslip.systemSlip);
  const divisor = useSelector((state: RootState) => state.betslip.divisor);
  const lastSlip = useSelector((state: RootState) => state.betslip.lastSlip);
  const tax = useSelector((state: RootState) => state.betslip.tax);
  const customerId = useSelector((state: RootState) => state.auth.customerId);


  const showMatchTracker = useSelector((state: RootState) => state.events.matchTrackerSidebar.showMatchTracker);
  const matchTrackerMinimized = useSelector((state: RootState) => state.events.matchTrackerSidebar.matchTrackerMinimized);

  const stakingRules = useSelector((state: RootState) => state.content.stakingRules);
  const registerBet   = useSelector((state: RootState) => state.betslip.registerBet);
  const outcomes = useSelector((state: RootState) => {
    const oldOutcomes = [];
    for (const bet of state.betslip.bets){
      oldOutcomes.push(bet.marketOutcomeCode);
    }
    return oldOutcomes;
  });
  const totalCombinations = useSelector(select.betslip.totalCombinations);
  const selectedSlip = useSelector(select.betslip.selectedSlip);
  const totalAmount = useSelector(select.betslip.totalAmount);
  const combinations = useSelector(select.betslip.combinations);
  const [isBetRegisteredOpen, setIsBetRegisteredOpen] = useState('');
  const [isClipboardOpen, setIsClipboardOpen] = useState(false);
  const [isCodeOpen, setIsCodeOpen] = useState(false);
  const [showPopupTax, setShowPopupTax] = useState(false);

  const dispatch = useDispatch<Dispatch>();

  useEffect(() => {
    const tillId = localStorage.getItem('tillId') ? localStorage.getItem('tillId') : -1;

    //THIS CALL NEEDS TO BE HERE SO THAT WE CAN CLOSE THE EVENTSOURCE, this will be done different when we have the live events.
    const response = new EventSource(`${ process.env.REACT_APP_BASE_URL
    }/bets/check-outcomes/${ outcomes.join(',') }?tillId=${ tillId }&customerId=${ customerId ? customerId : -1 }`);
    response.onmessage = (e) => dispatch.betslip.checkOutcomes(e.data);

    return () => {
      response.close();
    };
  }, [betsLength]);

  useEffect(() => {
    if (betsLength > 1 && shouldChangeSlip && lastSlip > 0 && slip !== lastSlip){
      dispatch.betslip.changeSlip(lastSlip);
    }
  }, [betsLength]);

  // STATE
  const [showComboWarning, setShowComboWarning] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [selectedInput, setSelectedInput] = useState(null);
  const [innerHeight, setInnerHeight] = useState(0);
  const [wrapperRefHeight, setWrapperRefHeight] = useState(null);
  const [validBetslip, setValidBetslip] = useState(true);
  const [invalidByHigherNumber, setInvalidByHigherNumber] = useState(false);
  const [totalCombinationsWithStake, setTotalCombinationsWithStake] = useState(0);
  const [showBanker, setShowBanker] = useState(false);
  const [maxHeight, setMaxHeight] = useState('100%');

  // REFS
  const wrapperRef = useRef(null);
  const virtualKeyboardRef = useRef(null);
  const formSlipRef = useRef(null);
  const permutationsRef = useRef(null);

  // CONTEXTS
  const { RETAIL_MODE } = useContext(AppModeContext);

  //#region FUNCTIONS
  const onSlipChange = useCallback(async (event, value: number) => {
    const haveCombo = _.find(bets, (bet) => bet.permutationsCount > 1 || bet.repetitionsCount > 1);

    if (haveCombo && !showComboWarning) {
      setShowComboWarning(true);
    } else if (!haveCombo && showComboWarning) {
      setShowComboWarning(false);

      if (slip !== value) {
        dispatch.betslip.changeSlip(value);
      }
    } else if (!haveCombo) {
      if (slip !== value) {
        dispatch.betslip.changeSlip(value);
      }
    }
  }, [betsLength, slip]);

  const onSlipSubmit = useCallback(async event => {
    event.preventDefault();

    const validCustomer = await dispatch.auth.checkUser();

    if (validCustomer){
      const submitResponse = await dispatch.betslip.submitBets(RETAIL_MODE);
      if (submitResponse && submitResponse.redirect) {
        history.push(submitResponse.redirect);
      }
    }
  }, []);

  const scrollToPermutations = useCallback(() => {
    if (formSlipRef.current && permutationsRef.current && slip === 2) {
      setTimeout(() => {
        permutationsRef.current?.scrollIntoView(
          {
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest'
          });
      }, 100);
    }
  }, []);

  const listenToScroll = () => {
    if (
      window.innerHeight !== innerHeight ||
      (wrapperRef && wrapperRef.current?.getBoundingClientRect().height !== wrapperRefHeight)
    ) {
      setInnerHeight(window.innerHeight);
      setWrapperRefHeight(wrapperRef ? wrapperRef.current?.getBoundingClientRect().height : null);
    }
  };

  const numberOfSelectedBetLines = useCallback(() => {
    let totalNumberOfCombinations = 0;
    for (const selected in Object.values(combinations)){
      if (systemSlip.selectedCombinations.includes(+selected +1)){
        totalNumberOfCombinations += Object.values(combinations)[selected].length;
      }
    }
    return totalNumberOfCombinations;
  }, [combinations]);
  //#endregion

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (slip < 2) {
        onSlipChange(null, slip + 1);
      }
    },
    onSwipedRight: () => {
      if (slip > 0) {
        onSlipChange(null, slip - 1);
      }
    }
  });

  const context = {
    showKeyboard,
    setShowKeyboard,
    selectedInput,
    setSelectedInput
  };

  //#region EFFECTS
  useEffect(() => {
    window.addEventListener('scroll', listenToScroll);
    window.addEventListener('resize', listenToScroll);

    if (permutationsRef.current && selectedSlip.stakeSelection === 'differentStake') {
      setTimeout(() => {
        formSlipRef.current?.scroll(0, permutationsRef.current?.offsetTop - formSlipRef.current?.offsetTop);
      }, 100);
    }

    return () => {
      window.removeEventListener('scroll', listenToScroll);
      window.removeEventListener('resize', listenToScroll);
    };
  }, []);

  useMemo(() => {
    const resizeObs = new ResizeObserver(listenToScroll);

    if (wrapperRef.current) {
      resizeObs.observe(wrapperRef.current);
    }

    return () => {
      resizeObs.unobserve(wrapperRef.current);
    };
  }, [wrapperRef.current]);

  useMemo(() => {
    let timeout;

    if (showComboWarning) {
      timeout = setTimeout(
        () => setShowComboWarning(false),
        5000
      );
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [showComboWarning]);

  useMemo(() => {
    scrollToPermutations();
  }, [combinations]);

  // Effect to check betslip staking validity
  useMemo(() => {
    let valid = true;

    // Check right away whether total value fails to meet betslip rules
    let _slip = 'single';
    if (slip === 1) _slip = 'acca';
    else if (slip === 2) _slip = 'system';
    let totalOfMinimum = stakingRules.sports[_slip].minBetslip;

    if (_.some(bets, (bet) => bet.permutationsCount > 1) && _slip === 'single'){
      if (selectedSlip.stakeSelection === 'sameStake') {

        totalOfMinimum = totalOfMinimum * betsLength;
      } else {
        for (let i=0; i<betsLength;i+=1){
          if ((selectedSlip.stakes[i] / divisor) < totalOfMinimum){
            valid = false;
          }
        }
      }
    }

    if (totalAmount < totalOfMinimum) {
      // totalAmount is too low
      valid = false;
      setInvalidByHigherNumber(false);
    }
    // } else if (totalAmount > stakingRules.sports[_slip].maxBetslip) {
    //   // totalAmount is too high
    //   valid = false;
    //   setInvalidByHigherNumber(true);
    // }

    if (valid === true) {
      if (selectedSlip.stakeSelection === 'sameStake') {
        const minStake = _slip === 'system' ? 1 : stakingRules.sports[_slip].minimum;

        if ((selectedSlip.sameStake / divisor) < minStake) {
          // bet stake is too low
          valid = false;
          setInvalidByHigherNumber(false);
        } else if ((selectedSlip.sameStake / divisor) > stakingRules.sports[_slip].maximum) {
          // bet stake is too high
          valid = false;
          setInvalidByHigherNumber(true);
        }

        for (const stake of selectedSlip.stakes) {
          if ((stake / divisor) < stakingRules.sports[_slip].minimum) {
            // bet stake is too low
            valid = false;
            setInvalidByHigherNumber(false);
          } else if ((stake / divisor) > stakingRules.sports[_slip].maximum) {
            // bet stake is too high
            valid = false;
            setInvalidByHigherNumber(true);
          }
        }

      } else if (selectedSlip.stakeSelection === 'differentStake') {
        let sumOfStakes = 0;
        const minStake = _slip === 'system' ? 1 : totalOfMinimum;

        for (const stake of selectedSlip.stakes) {
          sumOfStakes+=stake;

          if ((stake / divisor) < minStake) {
            // bet stake is too low
            valid = _slip === 'system' && (stake > 100 || stake === 0);
            setInvalidByHigherNumber(false);
          }
          // } else if ((stake / divisor) > stakingRules.sports[_slip].maximum) {
          //   // bet stake is too high
          //   valid = false;
          //   setInvalidByHigherNumber(true);
          // }

          if (valid === false) {
            break;
          }
        }
        if (_slip === 'system' && sumOfStakes === 0) valid = false; // we can have system line with 0 but they can't all be 0
      } else if (selectedSlip.stakeSelection === 'totalStake') {

        const stakesLength = _slip !== 'system' ? numberOfSelectedBetLines() : _.filter(selectedSlip.stakes, stake => stake > 0).length;
        const rule = _slip !== 'system' ? stakingRules.sports[_slip].minimum : totalOfMinimum;
        if ((selectedSlip.totalStake / divisor)/stakesLength < rule) {
          // bet stake is too low
          valid = false;
          setInvalidByHigherNumber(false);
        }
        // } else if ((selectedSlip.totalStake / divisor)/stakesLength > stakingRules.sports[_slip].maximum) {
        //   // bet stake is too high
        //   valid = false;
        //   setInvalidByHigherNumber(true);
        // }

      } else if (selectedSlip.stakeSelection === 'stakePerSystem' &&
       _.some(selectedSlip.stakes, (stake) => stake / divisor > 0 && stake / divisor < 1)){
        valid = false;
        setInvalidByHigherNumber(false);
      }
    }

    if (valid != validBetslip) {
      setValidBetslip(valid);
    }
  }, [bets, selectedSlip, totalAmount]);

  useMemo(() => {
    setTotalCombinationsWithStake(_.filter(selectedSlip.stakes, line => line > 0).length);
    setShowBanker(totalCombinationsWithStake >= 2);
  }, [selectedSlip]);

  useMemo(() => {
    if (wrapperRef) {
      let maxSize = !isMobile ?
        (innerHeight - wrapperRef.current?.getBoundingClientRect().y - 242) : (innerHeight - 330);

      if (!_.isNull(virtualKeyboardRef.current)) {
        maxSize -= virtualKeyboardRef.current.getBoundingClientRect().height;
      }

      if (showMatchTracker && !matchTrackerMinimized && maxSize > 255){
        setMaxHeight(maxSize - 245 + 'px');
      } else if (showMatchTracker && matchTrackerMinimized){
        setMaxHeight(maxSize - 20 + 'px');
      }
      if (maxSize > 105) {
        setMaxHeight(maxSize + 'px');
      }

      if (maxSize > 105 && RETAIL_MODE) {
        //remove height of footer
        setMaxHeight(maxSize - 112 + 'px');
      }
    }

  }, [bets, wrapperRef, showKeyboard, innerHeight, selectedInput, matchTrackerMinimized, showMatchTracker, maxHeight, virtualKeyboardRef.current]);

  const fixStakes = () => {
    let _slip = 'single';
    if (slip === 1) _slip = 'acca';
    else if (slip === 2) _slip = 'system';

    let totalOfMinimum = stakingRules.sports[_slip].minBetslip;
    if (_.some(bets, (bet) => bet.permutationsCount > 1)){
      const betWithPermutations = _.filter(bets, (bet) => bet.permutationsCount > 1);
      const permutations = [];
      for (const bet of betWithPermutations){
        permutations.push(bet.permutationsCount);
      }
      totalOfMinimum = totalOfMinimum * _.max(permutations);
    }

    if (selectedSlip.stakeSelection === 'totalStake') {

      if (totalAmount >= stakingRules.sports[_slip].minBetslip || totalAmount <=  stakingRules.sports[_slip].maxBetslip){
        let total = 0;
        const maximumError = totalAmount > stakingRules.sports[_slip].maximum * selectedSlip.stakes.length;

        if (maximumError){
          total += stakingRules.sports[_slip].maximum * selectedSlip.stakes.length;
        } else {
          total += _slip !== 'system' ?
            totalOfMinimum * selectedSlip.stakes.length :
            totalOfMinimum * numberOfSelectedBetLines();
        }

        dispatch.betslip.updateBetStake({ key: 'totalStake', value: total });
        return;
      } else if (totalAmount < totalOfMinimum) {
      // totalAmount is too low
        dispatch.betslip.updateBetStake({ key: 'totalStake', value: totalOfMinimum });
        return;
      } else if (totalAmount > stakingRules.sports[_slip].maxBetslip) {
      // totalAmount is too high
        dispatch.betslip.updateBetStake({ key: 'totalStake', value: stakingRules.sports[_slip].maxBetslip });
        return;
      }
    } else if (selectedSlip.stakeSelection === 'sameStake') {
      const minStake = _slip === 'system' ? 1 : totalOfMinimum;
      if ((selectedSlip.sameStake / divisor) < minStake) {
        // bet stake is too low
        dispatch.betslip.updateBetStake({ key: 'sameStake', value: minStake });
        return;
      } else if ((selectedSlip.sameStake / divisor) > stakingRules.sports[_slip].maximum) {
        // bet stake is too high
        dispatch.betslip.updateBetStake({ key: 'sameStake', value: stakingRules.sports[_slip].maximum });
        return;
      }
    } else if (selectedSlip.stakeSelection === 'differentStake') {
      const minStake = _slip === 'system' ? 1 : totalOfMinimum;
      for (let i = 0; i < selectedSlip.stakes.length; i++) {
        if ((selectedSlip.stakes[i] / divisor) < minStake) {
          // bet stake is too low
          if (_slip === 'system' && selectedSlip.stakes[i] !== 0) {
            dispatch.betslip.updateBetStake({ index: i, value: minStake });
          }

          if (_slip !== 'system' && selectedSlip.stakes[i] / divisor < minStake){
            const min = stakingRules.sports[_slip].minBetslip;
            dispatch.betslip.updateBetStake({ index: i, value: min * bets[i].permutationsCount });
          }

          if (_slip === 'system' && i === numberOfSelectedBetLines() -1 && !selectedSlip.stakes.some((stake) => stake > 0)){
            dispatch.betslip.updateBetStake({ index: i, value: minStake });
          }

          if (_slip !== 'system' && selectedSlip.stakes[i] < 1){
            dispatch.betslip.updateBetStake({ index: i, value: minStake });
          }
        } else if ((selectedSlip.stakes[i] / divisor) > stakingRules.sports[_slip].maximum) {
          // bet stake is too high
          dispatch.betslip.updateBetStake({ index: i, value: stakingRules.sports[_slip].maximum });
        }
      }
    } else if (selectedSlip.stakeSelection === 'stakePerSystem'){

      for (const selected in Object.values(combinations)){

        if (systemSlip.selectedCombinations.includes(+selected +1)){
          const numberOfPermutations = Object.values(combinations)[selected].length;

          if (systemSlip.stakePerSystem[selected] / numberOfPermutations < 100 && systemSlip.stakePerSystem[selected] / numberOfPermutations >= 0){
            dispatch.betslip.updateBetStake({ index: +selected, value: 1 });
          }
        }
      }

    }
  };

  useMemo(() => {
    // When adding a bet, if invalid insert minimum stake, else use default stake
    let _slip = 'single';
    if (slip === 1) _slip = 'acca';
    else if (slip === 2) _slip = 'system';
    if (selectedSlip.stakeSelection === 'totalStake' &&
    selectedSlip.totalStake < stakingRules.sports[_slip].minBetslip * divisor * +totalCombinations) {
      fixStakes();
    }
  }, [totalCombinations]);

  useMemo(() => {
    // different stake will get the values of totalStake or sameStake stakes
    if (slip === 2 && selectedSlip.stakeSelection !== 'differentStake'){
      const updatedStakes = [];
      let lastIndex = 0;
      const selectedCombinations = numberOfSelectedBetLines();

      for (const selected in Object.values(combinations)){
        for (let i=0; i<Object.values(combinations)[+selected].length; i++){
          if (systemSlip.selectedCombinations.includes(+selected +1)){
            let value:number = (Object.values(combinations)[selected][i]['stake']/100);

            if (selectedSlip.stakeSelection === 'totalStake' || selectedSlip.stakeSelection === 'stakePerSystem' ||
             selectedSlip.stakeSelection === 'sameStake'){
              const totalStakeValue = selectedSlip.stakeSelection !== 'totalStake' ? systemSlip.stakePerSystem[selected] : selectedSlip.totalStake;
              const numberOfLines = selectedSlip.stakeSelection !== 'totalStake' ?
                Object.values(combinations)[selected].length :
                selectedCombinations;
              value = totalStakeValue / numberOfLines;
              if (selectedSlip.stakeSelection === 'sameStake'){
                value = systemSlip.sameStake / divisor;
              }

              if (selectedSlip.stakeSelection === 'stakePerSystem'){
                value = totalStakeValue / divisor;
              }
            }

            updatedStakes.push({ index: lastIndex, value });
          } else {
            updatedStakes.push({ index: lastIndex, value: 0 });
          }
          lastIndex+=1;
        }
      }

      dispatch.betslip.updateMultipleSystemStakes(updatedStakes);
    }
  }, [selectedSlip.stakeSelection, selectedSlip.totalStake, systemSlip.stakePerSystem, systemSlip.sameStake, betsLength]);
  //#endregion

  const handleOnshare = () => {
    setIsClipboardOpen(true);
    setIsBetRegisteredOpen('');
  };

  const handleRegisterBet = useCallback(async () => {
    const betid = await dispatch.betslip.registerBet();
    if (betid){
      setIsBetRegisteredOpen(betid.toString());
    }
  }, []);

  const openCode = useCallback(() => {
    setIsCodeOpen(true);
  }, []);

  const clearBetslip = useCallback(() => {
    dispatch.betslip.clearBets() ;
  }, []);

  return (
    <VirtualKeyboardContext.Provider value={ context }>
      <div className="betslip-wrapper" ref={ wrapperRef }>
        {
          showComboWarning && <div className="betslip-wrapper__warning-message">
            { t('betslip.comboWarning') }
          </div>
        }

        <div className="betslip-wrapper__tabs">
          <button
            type="button"
            value={ 0 }
            onClick={ (e) => onSlipChange(e, 0) }
            className={
              classNames({
                'betslip-wrapper__tab': true,
                'betslip-wrapper__tab--selected': slip === 0
              })
            }
          >{ t('betslip.singleTab') }</button>
          <button
            type="button"
            value={ 1 }
            onClick={  (e) => onSlipChange(e, 1) }
            className={
              classNames({
                'betslip-wrapper__tab': true,
                'betslip-wrapper__tab--selected': slip === 1
              })
            }
            disabled={ showComboWarning }
          >{ t('betslip.accumulatorTab') }</button>
          <button
            type="button"
            value={ 2 }
            onClick={  (e) => onSlipChange(e, 2) }
            className={
              classNames({
                'betslip-wrapper__tab': true,
                'betslip-wrapper__tab--selected': slip === 2
              })
            }
            disabled={ showComboWarning }
          >{ t('betslip.systemTab') }</button>
        </div>

        <div className="betslip-wrapper__tab-panel">
          <form className="slip-form" onSubmit={ onSlipSubmit } { ...handlers }>
            <div className="slip-form__bets-list" ref={ formSlipRef } style={ { maxHeight } }>
              <BetsList
                showCombinations={ true }
                permutationsRef={ permutationsRef }
                totalCombinationsWithStake={ totalCombinationsWithStake }
                showBanker={ showBanker }
              />
              { /* <div className="slip-form__combinations">
                <div className="slip-form__combination-sub-container">
                  <Icon icon="trash" className="slip-form__clear" onClick={ clearBetslip } />
                  {
                    slip === 2 ? (
                      <span className="slip-form__combinations-title">
                        { t('betslip.selectedCombinations') }: { totalCombinationsWithStake }/{  totalCombinations }
                      </span>
                    )
                      :
                      (
                        <span className="slip-form__combinations-title">
                          { t('betslip.combinationsLabel') }: { totalCombinationsWithStake }
                        </span>
                      )
                  }
                </div>
                <a
                  href={ stakes.length > 0 ? '#' : null }
                  className="slip-form__combinations-link"
                  onClick={ onPermutationsClick }
                >
                  { t(`betslip.${ showCombinations === true ? 'hideCombinations' : 'showCombinations' }`) }
                </a>
              </div> */ }
            </div>
            {
              isBetRegisteredOpen && (
                <BetslipPopup
                  onClose={ () => setIsBetRegisteredOpen('') }
                  title={ t('betslip.betRegistered') }
                >
                  <BetslipPopup.BetRegistered
                    onClose={ () => setIsBetRegisteredOpen('') }
                    onShare={ handleOnshare }
                    code={ isBetRegisteredOpen }
                  />
                </BetslipPopup>
              )
            }

            {
              isClipboardOpen && (
                <BetslipPopup
                  onClose={ () => setIsClipboardOpen(!isClipboardOpen) }
                  title={ t('betslip.betRegistered') }
                >
                  <BetslipPopup.Clipboard />
                </BetslipPopup>
              )
            }

            {
              isCodeOpen && (
                <BetslipPopup
                  onClose={ () => setIsCodeOpen(!isCodeOpen) }
                  title={ t('betslip.loadBet') }
                >
                  <BetslipPopup.Code placeFunction={ () => setIsCodeOpen(false) } />
                </BetslipPopup>
              )
            }

            {
              showPopupTax && (
                <BetslipPopup
                  onClose={ () => setShowPopupTax(false) }
                  title={ t('betslip.taxInfo') }
                >
                  <BetslipPopup.Message
                    content={ t('betslip.taxMessage', { taxPercentage: tax.taxPercentage }) }
                  />
                </BetslipPopup>
              )
            }

            <div
              className={
                classNames(
                  'betslip-wrapper__footer',
                  { 'betslip-wrapper__footer--sticky': maxHeight !== '100%' }
                )
              }
            >
              <StakeOptions valid={ validBetslip } />
              <RegisterBet
                onLoadBet={ openCode }
                onClick={ handleRegisterBet }
              />

              <VirtualKeyboard ref={ virtualKeyboardRef } />
              <div className="betslip-wrapper__options">
                <Icon icon="trash" onClick={ clearBetslip } className="betslip-wrapper__options--cog" />
                { !registerBet && <SubmitButton valid={ validBetslip } maximum={ invalidByHigherNumber } fixStakes={ fixStakes } /> }
                { registerBet && <RegisterBetButton /> }
              </div>
              { tax.taxType === 'win' && <BetslipTax handleClick={ () => setShowPopupTax(true) } /> }
              <BetslipOptions />
            </div>
          </form>

        </div>
      </div>
    </VirtualKeyboardContext.Provider>
  );
}

export default memo(Betslip);
